import React, { useEffect, useState } from "react";
import { X, MoreVertical, Download, Check, ChevronUp } from "lucide-react";

const DISMISS_KEY = "mt_pwa_dismiss_until";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isAndroid() {
  if (typeof window === "undefined") return false;
  return /Android/i.test(window.navigator.userAgent || "");
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch (_e) {
    return false;
  }
}

function rememberDismiss(days = 14) {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + days * 24 * 60 * 60 * 1000)
    );
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Visual Android install guide. Slides up from the bottom on Android browsers,
 * showing three illustrated steps. When the browser supports
 * `beforeinstallprompt`, a single "Installa ora" button triggers the native
 * dialog directly. Otherwise the user is guided through the menu path.
 */
export const AndroidInstallGuide = () => {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [previouslyDismissed, setPreviouslyDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;
    if (!isAndroid()) return undefined;

    setEligible(true);
    setPreviouslyDismissed(Boolean(isDismissed()));

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setOpen(false);
      rememberDismiss(365);
    };
    window.addEventListener("appinstalled", onInstalled);

    let t;
    if (!isDismissed()) {
      t = setTimeout(() => setOpen(true), 6000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const close = () => {
    rememberDismiss(14);
    setOpen(false);
    setPreviouslyDismissed(true);
  };

  const installNow = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") rememberDismiss(365);
      else rememberDismiss(14);
    } catch (_e) {
      /* ignore */
    }
    setOpen(false);
    setDeferred(null);
  };

  if (!eligible) return null;

  return (
    <>
      {/* Re-open pill for users who dismissed earlier */}
      {!open && previouslyDismissed && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="android-install-reopen"
          className="mt-btn fixed left-1/2 -translate-x-1/2 bottom-4 z-40 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-xl"
          style={{
            background: "rgba(20,17,13,0.92)",
            color: "var(--mt-amber-bright)",
            border: "1px solid rgba(212,160,91,0.45)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Download size={14} strokeWidth={2.4} />
          Installa app
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center px-3 sm:px-6"
          data-testid="android-install-guide"
          role="dialog"
          aria-modal="true"
          aria-label="Installa MADRETERRA radio"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Chiudi"
            className="absolute inset-0"
            style={{
              background: "rgba(8,6,4,0.65)",
              backdropFilter: "blur(6px)",
            }}
          />

          <div
            className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden mt-rise"
            style={{
              background:
                "linear-gradient(180deg, rgba(28,24,19,0.98) 0%, rgba(20,17,13,0.98) 100%)",
              border: "1px solid rgba(212,160,91,0.30)",
              boxShadow: "0 -30px 80px rgba(0,0,0,0.6)",
            }}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Chiudi"
              data-testid="android-install-close"
              className="mt-btn absolute top-3 right-3 w-9 h-9 rounded-full inline-flex items-center justify-center"
              style={{
                color: "var(--mt-text-muted)",
                background: "rgba(245,239,228,0.06)",
              }}
            >
              <X size={16} strokeWidth={2.2} />
            </button>

            {/* header */}
            <div className="px-6 sm:px-7 pt-7 pb-4 text-center">
              <div
                className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                style={{
                  background: "rgba(212,160,91,0.16)",
                  color: "var(--mt-amber-bright)",
                  border: "1px solid rgba(212,160,91,0.35)",
                }}
              >
                <Download size={26} strokeWidth={2} />
              </div>
              <h3
                className="font-display text-2xl leading-tight"
                style={{ color: "var(--mt-cream)" }}
              >
                Installa MADRETERRA radio
              </h3>
              <p
                className="text-sm mt-1.5"
                style={{ color: "var(--mt-text-muted)" }}
              >
                {deferred ? (
                  <>
                    Un solo tocco: <strong style={{ color: "var(--mt-cream)" }}>
                      icona dedicata
                    </strong>, schermo intero, parte all'istante.
                  </>
                ) : (
                  <>
                    Aggiungila come app dal menu del browser per averla{" "}
                    <strong style={{ color: "var(--mt-cream)" }}>
                      sempre a portata di mano
                    </strong>.
                  </>
                )}
              </p>
            </div>

            {/* steps (different copy if native prompt is available) */}
            {deferred ? (
              <ol className="px-5 sm:px-6 pb-3 space-y-2.5">
                <Step
                  n={1}
                  title={"Tocca \u201CInstalla ora\u201D"}
                  hint="qui sotto in ambra"
                  icon={<Download size={20} strokeWidth={2.4} />}
                  accent="amber"
                />
                <Step
                  n={2}
                  title={"Conferma con \u201CInstalla\u201D"}
                  hint="nella finestra di sistema"
                  icon={<Check size={20} strokeWidth={2.6} />}
                  accent="amber"
                />
                <Step
                  n={3}
                  title="Apri dalla home"
                  hint="L'icona ti aspetta sulla schermata principale"
                  icon={<Check size={20} strokeWidth={2.6} />}
                  accent="green"
                />
              </ol>
            ) : (
              <ol className="px-5 sm:px-6 pb-3 space-y-2.5">
                <Step
                  n={1}
                  title="Tocca il menu del browser"
                  hint="In alto a destra: tre puntini ⋮"
                  icon={<MoreVertical size={20} strokeWidth={2.4} />}
                  accent="amber"
                />
                <Step
                  n={2}
                  title={"Tocca \u201CInstalla app\u201D"}
                  hint={"oppure \u201CAggiungi a schermata Home\u201D"}
                  icon={<Download size={20} strokeWidth={2.4} />}
                  accent="amber"
                />
                <Step
                  n={3}
                  title={"Conferma con \u201CInstalla\u201D"}
                  hint="Pronto: l'icona è sulla home"
                  icon={<Check size={20} strokeWidth={2.6} />}
                  accent="green"
                />
              </ol>
            )}

            <div className="px-6 pb-6 pt-2 flex flex-col items-center">
              {!deferred && (
                <>
                  <div
                    className="text-[11px] uppercase tracking-[0.28em] font-semibold mb-1.5"
                    style={{ color: "var(--mt-amber)" }}
                  >
                    Inizia dal menu
                  </div>
                  <ChevronUp
                    size={28}
                    strokeWidth={2}
                    className="mt-bounce-up"
                    style={{ color: "var(--mt-amber-bright)" }}
                  />
                </>
              )}
              <button
                type="button"
                onClick={deferred ? installNow : close}
                data-testid={
                  deferred ? "android-install-now" : "android-install-dismiss"
                }
                className="mt-btn mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
                style={{
                  background: "var(--mt-amber)",
                  color: "#1a1410",
                }}
              >
                {deferred ? (
                  <>
                    <Download size={16} strokeWidth={2.4} />
                    Installa ora
                  </>
                ) : (
                  "Ho capito"
                )}
              </button>
              {deferred && (
                <button
                  type="button"
                  onClick={close}
                  className="mt-btn mt-2 text-xs"
                  style={{ color: "var(--mt-text-muted)" }}
                >
                  Più tardi
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Step = ({ n, title, hint, icon, accent }) => {
  const isGreen = accent === "green";
  return (
    <li
      className="flex items-start gap-3.5 rounded-2xl p-3 sm:p-3.5"
      style={{
        background: "rgba(245,239,228,0.025)",
        border: "1px solid rgba(245,239,228,0.06)",
      }}
    >
      <div
        className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0 font-bold text-sm"
        style={{
          background: isGreen
            ? "rgba(37,211,102,0.18)"
            : "rgba(212,160,91,0.18)",
          color: isGreen ? "#3ee07e" : "var(--mt-amber-bright)",
          border: isGreen
            ? "1px solid rgba(37,211,102,0.35)"
            : "1px solid rgba(212,160,91,0.35)",
        }}
      >
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="font-display text-base leading-tight"
            style={{ color: "var(--mt-cream)" }}
          >
            {title}
          </span>
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-md ml-auto shrink-0"
            style={{
              color: isGreen ? "#3ee07e" : "var(--mt-amber-bright)",
              background: isGreen
                ? "rgba(37,211,102,0.10)"
                : "rgba(212,160,91,0.10)",
              border: isGreen
                ? "1px solid rgba(37,211,102,0.25)"
                : "1px solid rgba(212,160,91,0.25)",
            }}
          >
            {icon}
          </span>
        </div>
        <p
          className="text-xs mt-0.5"
          style={{ color: "var(--mt-text-muted)" }}
        >
          {hint}
        </p>
      </div>
    </li>
  );
};

export default AndroidInstallGuide;
